import { useMemo, useState } from 'react';
import { formatTwoKTime, parseTwoKTime } from '../utils/twoKTime';
import { athleteGoalRows, workoutTarget } from '../utils/korzoChart';
import { GoalPercentPicker } from './KorzoTargetPanel';

export default function KorzoSelfProg({ athlete, className = '' }) {
  const [goalPercent, setGoalPercent] = useState('90');
  const [overrideTime, setOverrideTime] = useState('');

  const percent = Number(goalPercent);
  const overrideSeconds = parseTwoKTime(overrideTime);

  const rows = useMemo(() => {
    const fromProfile = athleteGoalRows(athlete);
    if (overrideSeconds) {
      return [
        { boat_class: 'This 2k', two_k_seconds: overrideSeconds },
        ...fromProfile.filter((row) => row.boat_class !== 'Default')
      ];
    }
    return fromProfile;
  }, [athlete, overrideSeconds]);

  const targets = useMemo(() => {
    if (!Number.isFinite(percent) || percent <= 0) return [];
    return rows
      .map((row) => {
        const target = workoutTarget({ twoKSeconds: row.two_k_seconds, goalPercent: percent });
        if (!target) return null;
        return { boat_class: row.boat_class, two_k_seconds: row.two_k_seconds, ...target };
      })
      .filter(Boolean);
  }, [rows, percent]);

  return (
    <div className={`rounded-xl border border-indigo-200 bg-white p-4 sm:p-5 ${className}`}>
      <h3 className="text-base sm:text-lg font-bold text-indigo-950">Target splits</h3>
      <p className="text-xs sm:text-sm text-indigo-800 mt-0.5">
        Pick a stroke rate to fill the chart percent, then keep it or type a different %. Uses your 2k goals, or a 2k you enter here.
      </p>

      <div className="mt-3">
        <GoalPercentPicker value={goalPercent} onChange={setGoalPercent} />
      </div>

      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">2k time (optional)</label>
        <input
          type="text"
          inputMode="decimal"
          placeholder="e.g. 7:00"
          value={overrideTime}
          onChange={(e) => setOverrideTime(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 w-full sm:max-w-xs text-sm bg-white font-mono"
        />
      </div>

      {targets.length > 0 ? (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-1 font-medium">Class</th>
              <th className="py-1 font-medium text-right">2k</th>
              <th className="py-1 font-medium text-right">Target split</th>
            </tr>
          </thead>
          <tbody>
            {targets.map((row) => (
              <tr key={row.boat_class} className="border-t border-indigo-100">
                <td className="py-1.5 text-indigo-950">{row.boat_class}</td>
                <td className="py-1.5 text-right font-mono text-gray-700">{formatTwoKTime(row.two_k_seconds)}</td>
                <td className="py-1.5 text-right font-mono font-semibold text-indigo-950">
                  {formatTwoKTime(row.target_split_seconds)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-gray-600 mt-3">
          Enter a 2k time above, or add a 2k goal on your profile, to generate splits.
        </p>
      )}
    </div>
  );
}
